/**
 * <wishlist-page>
 * Native renderer for the account "my wishlists" page. Replaces the Vue
 * template that rendered the sidebar, the active list, the item rows and the
 * share / rename / delete popups from `wishlistProvider`.
 *
 * Data source: window.wishlistProvider (localWishlist, userWishlists,
 * currentWishlist). Re-renders on `wishlist:updated` / `wishlist:ready`. All
 * wishlist mutations go straight to the provider; add-to-cart bridges to the
 * global cart controller via window.enrouteCart.
 *
 * Config (translations, locale prefix and share/icon asset URLs) is passed in
 * via a data-config attribute rendered by Liquid. (It is an attribute rather
 * than a <script> child because Vue, which owns the #app subtree this lives in,
 * strips <script> tags from its compiled template.)
 *
 * <wishlist-shared-page> extends WishlistPage and renders the public
 * "shared wishlist" page from window.wishlistProvider.sharedWishlist.
 */
(() => {
const esc = window._erj_.esc;

const SPINNER =
  `<div><div id="loader"><div class="loader-section">` +
  `<svg id="spinner" width="65px" height="65px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">` +
  `<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>` +
  `</svg></div></div></div>`;

class WishlistPage extends HTMLElement {
  connectedCallback() {
    const cfg = this._readConfig();
    this.t = cfg.i18n || {};
    this.lang = cfg.lang || "/";
    this.assets = cfg.assets || {};

    this.displayedId = null; // null => follow the active (current) wishlist
    this._pinned = false; // user explicitly picked a list to view

    this._onUpdate = () => this._render();
    window.addEventListener("wishlist:updated", this._onUpdate);
    window.addEventListener("wishlist:ready", this._onUpdate);

    this._onClick = (event) => this._handleClick(event);
    this.addEventListener("click", this._onClick);

    this._render();
  }

  disconnectedCallback() {
    window.removeEventListener("wishlist:updated", this._onUpdate);
    window.removeEventListener("wishlist:ready", this._onUpdate);
    this.removeEventListener("click", this._onClick);
  }

  get provider() {
    return window.wishlistProvider;
  }

  _readConfig() {
    const raw = this.getAttribute("data-config");
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch (e) {
      return {};
    }
  }

  // ---- State helpers ----

  _userWishlists() {
    const p = this.provider;
    return p && p.userWishlists && Array.isArray(p.userWishlists.wishlists) ? p.userWishlists.wishlists : [];
  }

  _displayed() {
    const p = this.provider;
    if (!p) return null;
    if (!this._pinned || this.displayedId == null) return p.currentWishlist;
    if (p.localWishlist && p.localWishlist.id === this.displayedId) return p.localWishlist;
    const found = this._userWishlists().find((w) => w.id === this.displayedId);
    return found || p.currentWishlist;
  }

  // ---- Interaction ----

  _handleClick(event) {
    const trigger = event.target.closest("[data-action]");
    if (!trigger || !this.contains(trigger)) return;

    const action = trigger.dataset.action;
    const p = this.provider;
    const d = trigger.dataset;

    // Popup open/close don't need preventDefault gymnastics but we keep anchors inert.
    event.preventDefault();

    switch (action) {
      case "select-list":
        this._pinned = true;
        this.displayedId = d.id || "";
        this._render();
        break;
      case "add-wishlist":
        p.addWishlist((msg) => console.log("wishlist:", msg));
        break;
      case "save-local":
        p.saveLocalWishlist();
        break;
      case "set-active":
        p.setDefault(d.localId, () => {}, (err) => console.log(err));
        break;
      case "open-share":
        this._openPopup("wishlist-shared-popup");
        break;
      case "open-delete":
        this._openPopup("wishlist-delete-popup");
        break;
      case "open-rename": {
        const input = this.querySelector("#wishlist-rename-popup .popup-field-input");
        const displayed = this._displayed();
        if (input && displayed) input.value = displayed.name || "";
        this._openPopup("wishlist-rename-popup");
        break;
      }
      case "confirm-delete": {
        const displayed = this._displayed();
        if (displayed) p.removeWishlist(displayed.localId, () => {}, (err) => console.log(err));
        this._closePopup("wishlist-delete-popup");
        // Stop pinning a list that no longer exists.
        this._pinned = false;
        this.displayedId = null;
        this._render();
        break;
      }
      case "confirm-rename": {
        if (trigger.classList.contains("disabled")) return;
        const input = this.querySelector("#wishlist-rename-popup .popup-field-input");
        const displayed = this._displayed();
        const name = input ? input.value.trim() : "";
        if (displayed && name) {
          if (displayed.id === p.currentWishlist.id) p.currentWishlist.name = name;
          p.setName(displayed.localId, name, () => {}, (err) => console.log(err));
        }
        this._closePopup("wishlist-rename-popup");
        break;
      }
      case "close-popup":
        this._closePopup(d.popup);
        break;
      case "copy-link":
        this._copy(d.text);
        break;
      case "quantity": {
        const item = this._findItem(d.id);
        const next = Number(d.quantity);
        if (item && item.available && item.exists && next >= 1) {
          item.quantity = next;
          p.newQuantity(item);
          this._render();
        }
        break;
      }
      case "remove-item": {
        const item = this._findItem(d.id);
        if (item) p.removeWishlistItem(item, () => {}, (err) => console.log(err));
        this._render();
        break;
      }
      case "add":
        window.enrouteCart?.addToCart?.({
          id: d.variantId,
          quantity: Number(d.quantity) || 1,
          productId: d.productId,
          handle: d.handle,
        });
        break;
      case "add-all": {
        if (trigger.classList.contains("disabled")) return;
        const displayed = this._displayed();
        if (displayed) window.enrouteCart?.addAllFromWishlistToCart?.(displayed);
        break;
      }
      default:
        break;
    }
  }

  _findItem(id) {
    const displayed = this._displayed();
    if (!displayed || !Array.isArray(displayed.wishlistItems)) return null;
    return displayed.wishlistItems.find((it) => it.id === id) || null;
  }

  _openPopup(id) {
    const el = this.querySelector(`#${id}`);
    if (el) {
      el.hidden = false;
      el.classList.remove("hidden");
    }
  }

  _closePopup(id) {
    const el = this.querySelector(`#${id}`);
    if (el) {
      el.classList.add("hidden");
      el.hidden = true;
    }
  }

  _copy(text) {
    if (text && navigator.clipboard) navigator.clipboard.writeText(text);
    const toast = this.querySelector("[data-success-toast]");
    if (toast) {
      toast.hidden = false;
      toast.classList.remove("hidden");
      window.clearTimeout(this._toastTimer);
      this._toastTimer = window.setTimeout(() => {
        toast.classList.add("hidden");
        toast.hidden = true;
      }, 3000);
    }
  }

  // ---- Render ----

  _render() {
    const p = this.provider;
    if (!p) return;
    const syncing = p.syncingLocalData || p.syncingUserData;
    const displayed = this._displayed();

    this.innerHTML =
      `<section class="account"><div class="container wishlist" id="hvgWishlist"><div class="wrapper">` +
      `<div class="wishlist-wrapper">` +
      this._sidebarHtml() +
      `<div class="wishlist-right flex-1">` +
      (syncing ? SPINNER : "") +
      (displayed && displayed.id ? this._activeHtml(displayed, syncing) : "") +
      `</div>` +
      `</div>` +
      this._popupsHtml(displayed) +
      `</div></div></section>`;
  }

  _sidebarHtml() {
    const p = this.provider;
    const t = this.t;
    const local = p.localWishlist;
    const hasLocal = local && local.id && local.id.length > 0;
    const displayedId = this._displayed() ? this._displayed().id : "";

    let html = `<div id="wishlist-sidebar" class="wishlist-side">`;

    if (hasLocal) {
      html +=
        `<div class="wishlist-title">${esc(t.saved_device)}</div>` +
        `<div class="wishlist-side-element">` +
        `<a href="#" data-action="select-list" data-id="${esc(local.id)}" class="wishlist-tabs${
          local.id === displayedId ? " displayed" : ""
        }">${esc(local.name)}` +
        (local.id === p.currentWishlist.id ? `<div class="tag">${esc(t.active)}</div>` : "") +
        `</a></div>`;
    }

    html += `<div class="wishlist-title">${esc(t.saved_account)}</div>`;

    this._userWishlists().forEach((item) => {
      html +=
        `<div class="wishlist-side-element">` +
        `<a href="#" data-action="select-list" data-id="${esc(item.id)}" class="wishlist-tabs${
          item.id === displayedId ? " displayed" : ""
        }">${esc(item.name)}` +
        (item.id === p.currentWishlist.id ? `<div class="tag">${esc(t.active)}</div>` : "") +
        `</a></div>`;
    });

    html +=
      `<div class="wishlist-side-element">` +
      `<a href="#" class="wishlist-tabs btn wishlist-add-button create-new" data-action="add-wishlist" id="wishlistAdd">` +
      `&plus; &nbsp; ${esc(t.create)}</a></div>`;

    html += `</div>`;
    return html;
  }

  _activeHtml(displayed, syncing) {
    const p = this.provider;
    const t = this.t;
    const items = Array.isArray(displayed.wishlistItems) ? displayed.wishlistItems : [];
    const isLocal = p.localWishlist && p.localWishlist.id === displayed.id;
    const isCurrent = p.currentWishlist.id === displayed.id;

    let buttons = `<div class="wishlist-btns">`;
    if (isLocal) {
      buttons +=
        `<a href="#" data-action="save-local" class="operate"><div class="hvg-tooltip">` +
        `<span class="btn-name">${esc(t.btn_save)}</span>` +
        `<span class="hvg-tooltiptext">${esc(t.tooltip2)}</span>` +
        `</div></a>`;
    } else {
      buttons += `<a href="#" data-action="open-rename" class="operate"><span class="btn-name">${esc(t.rename)}</span></a>`;
    }

    if (!isCurrent && !isLocal) {
      buttons +=
        `<a href="#" class="operate" data-action="set-active" data-local-id="${esc(displayed.localId)}">` +
        `<div class="hvg-tooltip"><span class="btn-name">${esc(t.btn_active)}</span>` +
        `<span class="hvg-tooltiptext">${esc(t.tooltip)}</span></div></a>`;
    }

    buttons +=
      `<a href="#" data-action="open-share" class="operate" style="width: 14px; height: 14px;">` +
      `<img class="btn-name" src="${esc(this.assets.share)}" alt="share wishlist" width="50" height="50"></a>`;

    if (!isCurrent && !isLocal) {
      buttons +=
        `<a style="width: 14px; height: 14px;" href="#" class="operate" data-action="open-delete">` +
        `<img class="btn-name" src="${esc(this.assets.delete)}" alt="remove / delete" width="50" height="50"></a>`;
    }
    buttons += `</div>`;

    let itemsHtml;
    if (items.length === 0) {
      itemsHtml = this._emptyHtml();
    } else {
      itemsHtml = items
        .filter((it) => it.deleting !== true)
        .map((it) => this._itemHtml(it, { syncing }))
        .join("");
    }

    const bottom = this._bottomHtml(displayed, items);

    return (
      `<div style="display: block; width: 100%;"><div class="wishlist-content wishlist-active">` +
      `<div class="top-user-grid"><div class="wishlist-header"><span>${esc(displayed.name)}</span> &#40;${items.length}&#41; </div>` +
      `<div class="wishlist-subheader">` +
      buttons +
      `<div class="wishlist-created"><span>${esc(t.created)}</span><span>${esc(
        displayed.created ? p.formatDate(displayed.created) : ""
      )}</span></div>` +
      `</div></div>` +
      `<div class="items wishlist-elements">` +
      itemsHtml +
      `</div>` +
      bottom +
      `</div></div>`
    );
  }

  _itemHtml(item, { syncing = false, interactive = true } = {}) {
    const t = this.t;
    const unavailable = !syncing && !item.available;
    const options = Array.isArray(item.options)
      ? item.options.filter((o) => o !== "Default Title").map((o) => esc(o)).join(", ")
      : "";
    const showCompare = item.compareAtPriceVal != null && item.compareAtPriceVal > item.priceVal;

    const quantityHtml = interactive
      ? `<div class="q grid vcenter-xs hcenter-xs q-change">` +
        `<a class="minus" data-action="quantity" data-id="${esc(item.id)}" data-quantity="${item.quantity - 1}">-</a>` +
        `<div class="val">${item.quantity}</div>` +
        `<a class="plus" data-action="quantity" data-id="${esc(item.id)}" data-quantity="${item.quantity + 1}">+</a>` +
        `</div>`
      : `<div class="q grid vcenter-xs hcenter-xs">` +
        `<a class="minus">-</a><div class="val">${item.quantity}</div><a class="plus">+</a>` +
        `</div>`;

    const addBtn = item.available
      ? `<a class="wishlist-item-add-btn" data-action="add" data-variant-id="${esc(item.variantId)}" data-quantity="${item.quantity}" data-product-id="${esc(item.productId)}" data-handle="${esc(item.handle)}">+&nbsp;${esc(t.add)}</a>`
      : `<a class="wishlist-item-add-btn disabled">+&nbsp;${esc(t.add)}</a>`;

    return (
      `<div class="item${unavailable ? " unavailable" : ""}">` +
      (item.exists
        ? `<a href="${esc(item.url)}" style="background-image: url(${esc(item.featured_image)})" class="thumb"></a>`
        : "") +
      `<div class="info"><div class="info-line"><div style="info-line-left"><div class="w-full flex">` +
      (item.exists ? `<a href="${esc(item.url)}" class="title">${esc(item.title)} </a>` : "") +
      (unavailable ? `<span class="tag">${esc(t.soldout)}</span>` : "") +
      `</div>` +
      (options ? `<div class="options"><span>${options}</span></div>` : `<div class="options"></div>`) +
      `</div>` +
      `<div class="prices-wrap">` +
      (showCompare ? `<div class="price compare-at">${item.compareAtPrice || ""}</div>` : "") +
      `<div class="price">${item.price || ""}</div>` +
      `</div></div>` +
      `<div class="info-line"><div class="grid"><div style="width: 60%;">` +
      quantityHtml +
      `</div></div>` +
      `<div class="flex-right">` +
      (interactive ? `<a href="#" class="wishlist-item-remove-btn" data-action="remove-item" data-id="${esc(item.id)}">${esc(t.remove)}</a>` : "") +
      addBtn +
      `</div></div></div></div>`
    );
  }

  _bottomHtml(displayed, items) {
    const t = this.t;
    if (!items.length) return "";
    const hasAvailable = items.filter((it) => it.deleting !== true).some((it) => it.available);
    return (
      `<div class="bottom"><div class="totals">` +
      `<div>${esc(t.subtotal)} : <span style="font-size: 16px;">${this.provider.totalFor(displayed) || ""}</span></div>` +
      `<div class="action"><a href="#" class="btn hvg-add-button create-new${hasAvailable ? "" : " disabled"}" data-action="add-all">&plus; &nbsp;${esc(t.add_all)}</a></div>` +
      `</div></div>`
    );
  }

  _emptyHtml() {
    const t = this.t;
    return (
      `<div class="wishlist-empty" id="wishlist-empty">` +
      `<div class="empty-wrapper"><div class="empty-logo"></div>${esc(t.empty)}</div></div>`
    );
  }

  // ---- Popups (replicate custom-popup DOM; wired natively) ----

  _popupsHtml(displayed) {
    const localId = displayed ? displayed.localId : "";
    return this._sharePopupHtml(localId) + this._successToastHtml() + this._deletePopupHtml() + this._renamePopupHtml();
  }

  _shareUrl(network, localId) {
    const base = `${window.location.origin}${window._erj_.localePath("pages/shared-wishlist")}?wishlist=${encodeURIComponent(localId || "")}`;
    if (network === "facebook") return `https://www.facebook.com/sharer.php?u=${base}`;
    if (network === "x") return `https://twitter.com/messages/compose?text=${base}`;
    if (network === "whatsapp") return `https://wa.me/?text=${base}`;
    return base;
  }

  _sharePopupHtml(localId) {
    const t = this.t;
    const plain = this._shareUrl("plain", localId);
    return (
      `<div class="popup-wrapper hidden mobile-bottom wishlist-share" id="wishlist-shared-popup" hidden>` +
      `<div class="popup-container">` +
      `<div class="popup-header hspace-between-xs"><div class="popup-title">${esc(t.sharing_via)}</div>` +
      `<div class="popup-close" data-action="close-popup" data-popup="wishlist-shared-popup">&times;</div></div>` +
      `<div class="popup-content"><div>` +
      `<div class="clipboard-wrapper">` +
      `<div class="clipboard-link"><a class="clipboard-share" data-action="copy-link" data-text="${esc(plain)}" title="Clipboard copy">${esc(plain)}</a></div>` +
      `<div class="clipboard-btn" data-action="copy-link" data-text="${esc(plain)}">${esc(t.copy_link)}</div>` +
      `</div>` +
      `<div class="sharing-btns">` +
      `<div class="sharing-element"><a href="${esc(this._shareUrl("facebook", localId))}" title="Facebook share" target="_blank"><img alt="Share on Facebook" src="${esc(this.assets.facebook)}"></a></div>` +
      `<div class="sharing-element"><a href="${esc(this._shareUrl("x", localId))}" title="Twitter share" target="_blank"><img alt="Share on X" src="${esc(this.assets.x)}"></a></div>` +
      `<div class="sharing-element"><a href="${esc(this._shareUrl("whatsapp", localId))}" title="Whatsapp Share" target="_blank"><img alt="Whatsapp Share" src="${esc(this.assets.whatsapp)}"></a></div>` +
      `</div></div></div>` +
      `<div class="popup-footer"> </div>` +
      `</div></div>`
    );
  }

  _successToastHtml() {
    const t = this.t;
    return (
      `<div class="popup-wrapper hidden" data-success-toast style="background: transparent" hidden>` +
      `<div id="succesCopy"><img src="${esc(this.assets.check)}" style="width: 33px; height: 33px">${esc(t.sharing_copied)}</div>` +
      `</div>`
    );
  }

  _deletePopupHtml() {
    const t = this.t;
    return (
      `<div class="popup-wrapper hidden" id="wishlist-delete-popup" hidden>` +
      `<div class="popup-container">` +
      `<div class="popup-header justify-center"><div class="popup-title">${esc(t.delete_confirm)}</div></div>` +
      `<div class="popup-footer">` +
      `<div class="popup-footer-btn btn-cancel" data-action="close-popup" data-popup="wishlist-delete-popup">${esc(t.cancel)}</div>` +
      `<div class="popup-footer-btn btn-confirm" data-action="confirm-delete">${esc(t.confirm)}</div>` +
      `</div></div></div>`
    );
  }

  _renamePopupHtml() {
    const t = this.t;
    return (
      `<div class="popup-wrapper hidden" id="wishlist-rename-popup" hidden>` +
      `<div class="popup-container">` +
      `<div class="popup-header justify-center"><div class="popup-title">${esc(t.rename_title)}</div></div>` +
      `<div class="popup-content"><input class="popup-field-input" maxlength="40"></div>` +
      `<div class="popup-footer">` +
      `<div class="popup-footer-btn btn-cancel" data-action="close-popup" data-popup="wishlist-rename-popup">${esc(t.cancel)}</div>` +
      `<div class="popup-footer-btn btn-confirm" data-action="confirm-rename">${esc(t.confirm)}</div>` +
      `</div></div></div>`
    );
  }
}

// ---- Shared wishlist page ----

class WishlistSharedPage extends WishlistPage {
  // Inherits connectedCallback / disconnectedCallback / provider / _readConfig.
  // connectedCallback sets this.t = cfg.i18n which is all we need here.

  _handleClick(event) {
    const trigger = event.target.closest("[data-action]");
    if (!trigger || !this.contains(trigger)) return;
    event.preventDefault();

    const action = trigger.dataset.action;

    if (action === "add") {
      window.enrouteCart?.addToCart?.({
        id: trigger.dataset.variantId,
        quantity: Number(trigger.dataset.quantity) || 1,
        productId: trigger.dataset.productId,
        handle: trigger.dataset.handle,
      });
    } else if (action === "add-all" && this.provider) {
      if (!trigger.classList.contains("disabled")) {
        window.enrouteCart?.addAllFromWishlistToCart?.(this.provider.sharedWishlist);
      }
    }
  }

  _render() {
    const provider = this.provider;
    const wishlist = (provider && provider.sharedWishlist) || { wishlistItems: [] };
    const items = Array.isArray(wishlist.wishlistItems) ? wishlist.wishlistItems : [];
    const t = this.t;

    const heading =
      `<h2>${esc(t.shared_title)}` +
      (wishlist.customerName
        ? ` <span class="h2">${esc(t.shared_created)} ${esc(wishlist.customerName)}</span>`
        : "") +
      `</h2>`;

    let body;
    if (provider && provider.syncingSharedData) {
      body = SPINNER;
    } else if (items.length === 0) {
      body = this._emptyHtml();
    } else {
      body = items.filter((i) => i.exists).map((i) => this._itemHtml(i, { interactive: false })).join("");
    }

    this.innerHTML =
      `<div class="wrapper">` +
      heading +
      `<div class="wishlist-wrapper shared">` +
      `<div id="wishlist-content" style="display: block; width: 100%;">` +
      `<div class="wishlist-content wishlist-active">` +
      `<div class="shared-wishlist-top">` +
      `<div class="wishlist-header"><span>${esc(wishlist.name)}</span> &#40;${items.length}&#41; </div>` +
      `<div class="wishlist-created"><h5>${esc(t.created)}</h5><h5>${esc(
        provider && wishlist.created ? provider.formatDate(wishlist.created) : ""
      )}</h5></div>` +
      `</div>` +
      `<div class="items wishlist-elements">` +
      body +
      this._bottomHtml(wishlist, items) +
      `</div></div></div></div></div>`;
  }

}

if (!window.customElements.get("wishlist-page")) {
  window.customElements.define("wishlist-page", WishlistPage);
}
if (!window.customElements.get("wishlist-shared-page")) {
  window.customElements.define("wishlist-shared-page", WishlistSharedPage);
}
})();
